import { useEffect, useState } from "react";
import { Space, Tag, Typography } from "antd"
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import qs from "query-string";
import utc from "dayjs/plugin/utc";
import { useDispatch } from "react-redux";

import { ListPageContainer } from '@/components';
import {appManagementActions, loadingActions, transactionActions} from '@/redux/actions';
import { CLIENT_APP_ID, DATETIME_FORMAT_DISPLAY, fieldTypes, statusTypes, storageKeys } from "@/constants";
import { formatDate } from "@/utils/date";
import { smsTransactionStatuses } from "@/constants/masterData";
import { useNotification } from "@/hooks";
import apiConfig from "@/constants/apiConfig";
import { getCookie } from "@/utils/localStorage";

import { FileExcelOutlined } from "@ant-design/icons";

dayjs.extend(utc);

const SMSListPage = () => {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const {showErrorMessage, showSuccessMessage} = useNotification();

    const [startDate, setStartDate] = useState(searchParams.get('startDate') || dayjs().startOf("month"));
    const [endDate, setEndDate] = useState(searchParams.get('endDate'));
    const [appList, setAppList] = useState([]);
    const [loading, setLoading] = useState(false);

    const mappingSearchParams = (searchFields) => {
        const filter = {};
        searchFields?.forEach(searchField => {
            if (searchParams.has(searchField.key)) {
                if (searchField.key === 'startDate') {
                    filter[searchField.key] = dayjs.utc(searchParams.get(searchField.key)).format();
                } else if (searchField.key === 'endDate') {
                    filter[searchField.key] = dayjs.utc(searchParams.get(searchField.key)).endOf("day").format();
                } else {
                    filter[searchField.key] = searchParams.get(searchField.key);
                }
            }
        });

        //default startDate when get list params
        if (!filter['startDate']) {
            const startDateTemp = dayjs.utc().startOf("month").format();
            if (startDate) {
                filter['startDate'] = startDateTemp;
            }
        }
        return filter;
    }

    const onResetSearch = () => {
        setStartDate( dayjs().startOf("month"));
    }

    const mappingDropdownData = (res) => {
        return res.data?.map(item => ({label: item.name, value: item.id}));
    }

    const searchFields = [
        {
            key: 'serviceId',
            searchPlaceholder: 'Service',
            fieldType: fieldTypes.SELECT,
            options: appList,
            loading: loading,
            gridCol: 6,
        },
        {
            key: 'phoneNumber',
            searchPlaceholder: 'Phone number',
            gridCol: 6,
        },
        {
            key: 'startDate',
            searchPlaceholder: 'Start date',
            fieldType: fieldTypes.DATE,
            gridCol: 6,
            initialValue: dayjs().startOf('months'),
            disabledDate: (current) => {
                //startDate chỉ tối đa 3 tháng gần nhất (trong quá khứ) so với ngày hiện tại
                //phải nhỏ hơn endDate
                //nhỏ hơn ngày hiện tại
                return current && (current < dayjs().add(-3, "months").startOf('day')
                    || current > dayjs(endDate).endOf?.('day')
                    || current > dayjs().endOf('day'));
            },
            onChange: (startDate) => setStartDate(startDate),
        },
        {
            key: 'endDate',
            searchPlaceholder: 'End date',
            fieldType: fieldTypes.DATE,
            gridCol: 6,
            disabledDate: (current) => {
                const currStartDate = startDate ? dayjs(startDate) : null;
                //endDate chỉ tối đa 3 tháng gần nhất (trong quá khứ) so với ngày hiện tại
                //phải lớn hơn startDate
                //nhỏ hơn ngày hiện tại
                return current && (current < dayjs().add(-3, "months").startOf('day')
                    || current < currStartDate?.startOf?.("day")
                    || current > dayjs().endOf('day'));
            },
            onChange: (endDate) => setEndDate(endDate),
        },
        {
            key: 'status',
            searchPlaceholder: 'Select Status',
            fieldType: fieldTypes.SELECT,
            options: smsTransactionStatuses,
            gridCol: 6,
        },
    ]

    const onExportExcel = (e, getList, list) => {
        if (!list || list?.length < 1) {
            e.preventDefault();
            showErrorMessage('Requires at least one row to export!')
            return;
        }

        dispatch(loadingActions.showLoadingFullScreen());

        dispatch(transactionActions.exportTransactions({
            params: mappingSearchParams(searchFields),
            onCompleted: data => {
                dispatch(loadingActions.hideLoadingFullScreen());
                const a = document.createElement("a");
                a.href = window.URL.createObjectURL(data);
                a.download = `TransactionList-${dayjs().format('DD-MM-YYYY')}.xlsx`;
                a.click();
            },
            onError: err => {
                dispatch(loadingActions.hideLoadingFullScreen());
                showErrorMessage("Export SMS Transactions failed. Please try again!");
            }
        }))
    }

    useEffect(() => {
        setLoading(true);
        dispatch(appManagementActions.getList({
            onCompleted: (res) => {
                setAppList(mappingDropdownData(res));
                setLoading(false);
            },
            onError: (err) => {
                setLoading(false);
            }
        }));
    }, [])

    return (
        <ListPageContainer
            objectName="App"
            breadcrumbs={[
                {breadcrumbName: 'Transaction'},
                {breadcrumbName: 'SMS'},
            ]}
            columns={[
                {title: 'Service', dataIndex: ['clientApp', 'name']},
                {title: 'Brand', dataIndex: 'brandName'},
                {title: 'Phone', dataIndex: 'phoneNumber'},
                {
                    title: 'ID Req. (bid)',
                    dataIndex: 'transNo',
                },
                {
                    title: 'ID Res. (msgid)',
                    dataIndex: 'partnerTransId',
                    render: (partnerTransId) => (
                        <Typography.Text ellipsis={{tooltip: partnerTransId}} style={{maxWidth: 150}}>
                            {partnerTransId}
                        </Typography.Text>
                    )
                },
                {
                    title: 'Message',
                    dataIndex: 'content',
                    render: (content) => (
                        <Typography.Paragraph ellipsis={{tooltip: content, rows: 2}}>
                            {content}
                        </Typography.Paragraph>
                    )
                },
                {title: 'Carrier', dataIndex: 'carrier'},
                {
                    title: 'Status',
                    dataIndex: 'status',
                    render: (status) => <Tag
                        style={{minWidth: 70, textAlign: 'center'}}
                        color={[statusTypes.SENT, statusTypes.SUBMITTED].includes(status) ? "success" : "error"}>
                        {status.replace("_", " ")}
                    </Tag>
                },
                {
                    title: 'Created at',
                    dataIndex: 'createdDate',
                    render: (createdDate) => {
                        const dateTime = formatDate(createdDate, DATETIME_FORMAT_DISPLAY);
                        const dateTimeSplit = dateTime ? dateTime.split(" ") : [];
                        return (
                            dateTimeSplit.length ?
                                <Space direction="vertical" size={1} style={{minWidth: 80}}>
                                    <Typography.Text>{dateTimeSplit[1]}</Typography.Text>
                                    <Typography.Text>{dateTimeSplit[0]}</Typography.Text>
                                </Space>
                                :
                                <Typography.Text>N/A</Typography.Text>
                        )
                    }
                },
                {
                    title: 'Sent at',
                    dataIndex: 'sentDate',
                    render: (sentAt) => {
                        const dateTime = formatDate(sentAt, DATETIME_FORMAT_DISPLAY);
                        const dateTimeSplit = dateTime ? dateTime.split(" ") : [];
                        return (
                            dateTimeSplit.length ?
                                <Space direction="vertical" size={1} style={{minWidth: 80}}>
                                    <Typography.Text>{dateTimeSplit[1]}</Typography.Text>
                                    <Typography.Text>{dateTimeSplit[0]}</Typography.Text>
                                </Space>
                                :
                                <Typography.Text>N/A</Typography.Text>
                        )
                    }
                },
            ]}
            searchFields={searchFields}
            buttonActions={[
                {
                    label: 'Export',
                    icon: <FileExcelOutlined/>,
                    ghost: true,
                    onClick: onExportExcel,
                }
            ]}
            mappingSearchParams={mappingSearchParams}
            getListAction={transactionActions.getSMSTransactions}
            onResetSearchParams={onResetSearch}
        >
        </ListPageContainer>

    )
}

export default SMSListPage;